import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { cn } from '@/shared/utils/cn';

const modalVariants = cva(
  'border-tertiary-20 relative flex transform flex-col overflow-hidden rounded-xl border bg-white shadow-xl transition-all duration-300 sm:rounded-2xl sm:shadow-2xl dark:border-gray-700/50 dark:bg-gradient-to-br dark:from-gray-800 dark:via-gray-800 dark:to-gray-900',
  {
    variants: {
      state: {
        open: 'translate-y-0 scale-100 opacity-100',
        closed: 'translate-y-4 scale-95 opacity-0',
      },
    },
    defaultVariants: {
      state: 'closed',
    },
  },
);

const backdropVariants = cva(
  'fixed inset-0 bg-black/40 transition-all duration-300 dark:bg-black/60',
  {
    variants: {
      state: {
        open: 'opacity-100 backdrop-blur-md',
        closed: 'opacity-0 backdrop-blur-none',
      },
    },
    defaultVariants: {
      state: 'closed',
    },
  },
);

const iconContainerVariants = cva(
  'flex flex-shrink-0 items-center justify-center rounded-lg text-white shadow-md sm:rounded-xl sm:shadow-lg',
  {
    variants: {
      variant: {
        primary: 'bg-primary-100 shadow-primary-20/50',
        danger: 'bg-gradient-to-br from-red-500 via-red-600 to-red-700',
        success: 'bg-gradient-to-br from-green-500 via-green-600 to-green-700',
        info: 'bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700',
        warning:
          'bg-gradient-to-br from-yellow-500 via-yellow-600 to-yellow-700',
      },
      size: {
        sm: 'h-7 w-7 sm:h-8 sm:w-8',
        md: 'h-8 w-8 sm:h-10 sm:w-10',
        lg: 'h-10 w-10 sm:h-12 sm:w-12',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

const BaseModal = ({
  isOpen,
  onClose,
  children,
  title,
  icon,
  iconVariant = 'primary',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className = '',
  size = 'md',
  footer,
  headerClassName = '',
  contentClassName = '',
}) => {
  const { isMobile } = useResponsive();
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      document.body.style.overflow = 'unset';
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && closeOnEscape) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen && !isAnimating) return null;

  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const sizeClasses = {
    sm: isMobile ? 'max-w-[95vw]' : 'max-w-sm sm:max-w-md',
    md: isMobile ? 'max-w-[95vw]' : 'max-w-md sm:max-w-lg',
    lg: isMobile ? 'max-w-[95vw]' : 'max-w-lg sm:max-w-xl lg:max-w-2xl',
    xl: isMobile ? 'max-w-[95vw]' : 'max-w-2xl sm:max-w-3xl lg:max-w-4xl',
    full: 'max-w-[95vw]',
  };

  const state = isOpen ? 'open' : 'closed';

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center',
        isMobile ? 'p-3' : 'p-4 sm:p-6',
        closeOnBackdropClick && 'cursor-pointer',
      )}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div className={backdropVariants({ state })}>
        <div className="from-primary-10/40 to-tertiary-10/20 dark:to-primary-900/10 absolute inset-0 bg-gradient-to-br via-transparent dark:from-gray-900/20 dark:via-transparent" />
      </div>

      <div
        className={cn(
          'relative z-10 mx-auto flex w-full cursor-default flex-col transition-all duration-300 ease-out',
          sizeClasses[size],
          isMobile ? 'max-h-[92vh]' : 'max-h-[90vh]',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={cn(modalVariants({ state }), className)}>
          <div
            className={cn(
              'absolute top-0 right-0 left-0 h-1',
              iconVariant === 'primary' && 'bg-primary-100',
              iconVariant === 'danger' &&
                'bg-gradient-to-r from-red-500 via-red-600 to-red-500',
              iconVariant === 'success' &&
                'bg-gradient-to-r from-green-500 via-green-600 to-green-500',
              iconVariant === 'info' &&
                'bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500',
              iconVariant === 'warning' &&
                'bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500',
            )}
          />

          {showCloseButton && (
            <button
              onClick={onClose}
              className={cn(
                'bg-tertiary-10/80 text-tertiary-80 hover:bg-tertiary-20 hover:text-tertiary-100 absolute z-10 flex items-center justify-center rounded-lg transition-all duration-200 dark:bg-gray-700/80 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white',
                isMobile
                  ? 'top-2 left-2 h-7 w-7'
                  : 'top-3 left-3 h-8 w-8 sm:top-4 sm:left-4 sm:h-9 sm:w-9',
              )}
              aria-label="بستن"
            >
              <X size={isMobile ? 16 : 18} />
            </button>
          )}

          {title && (
            <div
              className={cn(
                'border-tertiary-20 from-tertiary-10/50 flex flex-shrink-0 items-center gap-2 border-b bg-gradient-to-r to-transparent backdrop-blur-sm sm:gap-3 dark:border-gray-700/30 dark:from-gray-700/20',
                headerClassName,
                isMobile ? 'p-3' : 'p-4 sm:p-5',
              )}
            >
              {icon && (
                <div
                  className={iconContainerVariants({
                    variant: iconVariant,
                    size: isMobile ? 'sm' : 'md',
                  })}
                >
                  <div className="flex items-center justify-center">{icon}</div>
                </div>
              )}

              <h2
                id="modal-title"
                className={cn(
                  'text-tertiary-100 min-w-0 flex-1 truncate font-bold dark:text-gray-100',
                  isMobile ? 'text-sm' : 'text-base sm:text-lg',
                )}
              >
                {title}
              </h2>
            </div>
          )}

          <div
            className={cn(
              'relative min-h-0 flex-1 overflow-y-auto',
              contentClassName,
            )}
          >
            {children}
          </div>

          {footer && (
            <div
              className={cn(
                'border-tertiary-20 from-tertiary-10/30 flex flex-shrink-0 flex-col gap-2 border-t bg-gradient-to-r to-transparent backdrop-blur-sm sm:flex-row sm:gap-3 dark:border-gray-700/30 dark:from-gray-700/10',
                isMobile ? 'p-3' : 'p-4 sm:p-5',
              )}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

BaseModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  icon: PropTypes.node,
  iconVariant: PropTypes.oneOf([
    'primary',
    'danger',
    'success',
    'info',
    'warning',
  ]),
  showCloseButton: PropTypes.bool,
  closeOnBackdropClick: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'full']),
  footer: PropTypes.node,
  headerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
};

export default BaseModal;
